
























































































































































































import { Component, Prop, Mixins } from "vue-property-decorator";
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import { DataModules } from "@/store/modules/data";
import { mapState } from "vuex";
import { IState } from "@/store";
import { CreditCardTypes } from "@/data/constants";
import { IBrandGateway, IGateway } from "@/models/gateways";
import { GatewayTypes } from "@/data/enums";
import { IClient } from "@/models/clients";
import { IBrand } from "@/models/brands";
import { ICurrency } from "@/models/constants";
import { IAddress } from "@/models/addresses";
import _ from "@/boot/lodash";
import { IContractProduct } from "@/models/contracts";
import { IAccount } from "@/models/accounts";

interface IManualCardForm {
  card_type: string | null;
  card_expire_date: string;
  card_last4: string;
  card_token: string;
  address_id: null | IAddress["id"];
  gateway_id: null | IGateway["id"];
  contract_product_id?: null | IContractProduct["id"];
}

@Component({
  components: {
    "credit-card-form": () =>
      import("@/components/app/global/payments/creditCardForm.vue"),
    "client-address-select": () =>
      import("@/components/app/global/fields/clientAddressSelect.vue"),
    "select-client-contract-product": () =>
      import("@/components/app/global/fields/selectClientContractProduct.vue")
  },
  computed: {
    ...mapState({
      gateways(state, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.BRAND_GATEWAYS
        });
      },
      dataScope(state: IState, getters) {
        return getters[
          `data/${DataModules.CLIENTS_PAYMENT_DETAILS}/clientScope`
        ](this.clientId);
      }
    })
  }
})
export default class AddManualPaymentMethodModal extends Mixins(
  RequestPromises,
  HasValidation
) {
  // Props

  @Prop({ required: true })
  public readonly brandId!: IBrand["id"];

  @Prop({ required: true })
  public readonly clientId!: IClient["id"];

  @Prop({ required: true })
  public readonly accountId!: IAccount["id"];

  @Prop({ default: null })
  public readonly gatewayId!: IGateway["id"];

  @Prop({ default: "" })
  public readonly currencyId!: ICurrency["id"];

  @Prop({ default: false })
  public readonly storedOnly!: boolean;

  @Prop({ default: false })
  public readonly onlineOnly!: boolean;

  // Data
  public gateways!: IBrandGateway[];
  public isLoading = true;
  public requestError = false;
  public isReloading = false;
  public dataScope!: object;
  public creditCardTypes = CreditCardTypes;
  public isProcessing = false;

  public form: IManualCardForm = {
    gateway_id: this.gatewayId,
    address_id: null,
    card_type: null,
    card_expire_date: "",
    card_last4: "",
    card_token: "",
    contract_product_id: null
  };

  // Lifecycle methods
  public created() {
    this.getBrandGateways();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.BRAND_GATEWAYS,
      vm: this
    });
  }

  // Getters

  public get sanitizedForm() {
    const form = {
      ...(this.isCardType
        ? this.form
        : _.pick(this.form, [
            "address_id",
            "card_token",
            "contract_product_id"
          ])),
      gateway_id: this.form.gateway_id,
      brand_id: this.brandId
    };

    if (!this.showCProductSelector || _.isNull(form.contract_product_id))
      delete form.contract_product_id;

    return form;
  }

  public get cardClass() {
    const type = this.form.card_type;
    switch (type) {
      case "visa":
      case "mastercard":
      case "diners-club":
      case "discover":
      case "jcb":
      case "unionpay":
      case "maestro":
      case "elo":
      case "hipercard":
      case "american-express":
        return type;
      default:
        return "default";
    }
  }

  public get showCProductSelector() {
    return _.get(
      _.find(this.availableGateways, ["gateway.id", this.form.gateway_id]),
      "gateway.gateway_provider.external_billing_charge",
      false
    );
  }

  public get currencyFilter() {
    return this.currencyId
      ? {
          "filter[gateway.currencies.id]": this.currencyId
        }
      : {};
  }

  public get availableGateways() {
    return _.orderBy(
      _.filter(this.gateways, (g: IBrandGateway) => {
        return g.gateway.allow_manual_store;
      }),
      [brandGateway => brandGateway.order]
    );
  }

  public get needsAddress() {
    return (
      _.get(
        _.find(this.availableGateways, ["gateway_id", this.form.gateway_id]),
        "gateway.gateway_provider.needs_address"
      ) ?? true
    );
  }

  public get isCardType() {
    return (
      _.get(
        _.find(this.availableGateways, ["gateway_id", this.form.gateway_id]),
        "gateway.type"
      ) === GatewayTypes.CARD
    );
  }

  public get formIsDisabled() {
    return this.isProcessing || this.hasErrors;
  }

  // Methods

  public async getBrandGateways() {
    try {
      await this.$store.dispatch(`data/${DataModules.BRAND_GATEWAYS}/list`, {
        brandId: this.brandId,
        ignoreStored: this.isReloading,
        params: {
          limit: 0,
          order: "order",
          "filter[active]": 1,
          client_id: this.clientId,
          with: "gateway.gateway_provider,gateway.card_types",
          ...this.currencyFilter
        },
        vm: this
      });
      this.$emit("brand-gateways-loaded");
      this.requestError = false;
    } catch (error) {
      this.requestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public async validateBeforeSubmit() {
    this.isProcessing = true;
    this.validate()
      .then(async result => {
        if (!result) throw this.$t("_.form_invalid");
        await this.$store.dispatch(
          `data/${DataModules.CLIENTS_PAYMENT_DETAILS}/createManual`,
          {
            scope: this.dataScope,
            id: this.clientId,
            data: this.sanitizedForm
          }
        );
        this.$emit("payment-method-added");
        return this.$emit("close");
      })
      .catch(error => {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      })
      .finally(() => {
        this.isProcessing = false;
      });
  }
}
